import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { LuCircleCheck, LuSparkles, LuX } from 'react-icons/lu';

import { WizardProposal } from '../../entities/wizard-proposal';
import WizardPanel from '../chat/WizardPanel';

interface WizardLauncherProps {
  onClose: () => void;
}

// Always-available, chrome-free entry point for "start a new project from a spec" —
// reachable from the sidebar CTA at any time, not gated behind syncing an existing project.
export const WizardLauncher: React.FC<WizardLauncherProps> = ({ onClose }) => {
  const [built, setBuilt] = useState<WizardProposal | null>(null);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return createPortal(
    <motion.div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4 backdrop-blur-[2px]"
      role="presentation"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <motion.div
        className="flex w-full max-w-5xl max-h-[85vh] flex-col rounded-xl bg-base-100 shadow-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="wizard-launcher-title"
        initial={{ opacity: 0, y: 12, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 12, scale: 0.98 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
      >
        <div className="flex items-center justify-between px-5 py-4 shrink-0">
          <div className="flex items-center gap-2">
            <LuSparkles className="text-primary" />
            <span id="wizard-launcher-title" className="font-display font-semibold">
              New project from a spec
            </span>
          </div>
          <button className="btn btn-ghost btn-sm btn-circle" onClick={onClose} title="Close">
            <LuX size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 pt-0">
          {built && (
            <div className="mb-4 flex items-center gap-2 rounded-lg bg-success/10 px-3 py-2 text-sm text-success">
              <LuCircleCheck className="shrink-0" />
              <span>
                {built.projectName || 'Your project'} is ready on <b className="capitalize">{built.platform}</b>. Keep
                chatting below to add or change tasks.
              </span>
            </div>
          )}
          <WizardPanel onBuilt={setBuilt} />
        </div>
      </motion.div>
    </motion.div>,
    document.body,
  );
};

export default WizardLauncher;
